import { cn } from '../../lib/utils';

type SpinnerSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

interface SpinnerProps {
  size?: SpinnerSize;
  className?: string;
  label?: string;
}

const sizeMap: Record<SpinnerSize, string> = {
  xs: 'w-3 h-3 border-[1.5px]',
  sm: 'w-4 h-4 border-2',
  md: 'w-6 h-6 border-2',
  lg: 'w-8 h-8 border-[3px]',
  xl: 'w-12 h-12 border-4',
};

function Spinner({ size = 'md', className, label = 'Chargement...' }: SpinnerProps) {
  return (
    <div className={cn('inline-flex items-center justify-center', className)} role="status">
      <div
        className={cn(
          'rounded-full animate-spin',
          'border-leather-200 border-t-leather-600',
          sizeMap[size]
        )}
      />
      <span className="sr-only">{label}</span>
    </div>
  );
}

// Variante avec texte
interface SpinnerWithTextProps extends SpinnerProps {
  text?: string;
}

export function SpinnerWithText({ size = 'md', text = 'Chargement...', className }: SpinnerWithTextProps) {
  return (
    <div className={cn('flex flex-col items-center justify-center gap-3', className)}>
      <Spinner size={size} />
      <p className="font-sans text-sm text-leather-600 animate-pulse">{text}</p>
    </div>
  );
}

// Variante plein écran
interface FullPageSpinnerProps {
  text?: string;
}

export function FullPageSpinner({ text }: FullPageSpinnerProps) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-cream-50/80 backdrop-blur-sm">
      <SpinnerWithText size="xl" text={text} />
    </div>
  );
}

// Skeleton pour le chargement de contenu
interface SkeletonProps {
  className?: string;
  variant?: 'text' | 'circular' | 'rectangular';
  width?: string | number;
  height?: string | number;
}

export function Skeleton({ 
  className, 
  variant = 'rectangular',
  width,
  height 
}: SkeletonProps) {
  const baseStyles = 'animate-pulse bg-leather-200/60';
  
  const variantStyles: Record<string, string> = {
    text: 'rounded-md h-4',
    circular: 'rounded-full',
    rectangular: 'rounded-xl',
  };

  const style: React.CSSProperties = {
    width: width,
    height: height,
  };

  return (
    <div
      className={cn(baseStyles, variantStyles[variant], className)}
      style={style}
    />
  );
}

// Skeleton pour une carte produit
export function ProductCardSkeleton() {
  return (
    <div className="bg-cream-100 rounded-2xl overflow-hidden">
      <Skeleton className="aspect-square w-full" />
      <div className="p-4 space-y-3">
        <Skeleton variant="text" className="w-3/4" />
        <Skeleton variant="text" className="w-1/2" />
        <Skeleton variant="text" className="w-1/4 h-6" />
      </div>
    </div>
  );
}

export { Spinner };
export default Spinner;
